import type { FormEvent } from 'react'

import { useCallback, useMemo, useState } from 'react'

import type { ColumnConfig } from '@/components/tables/enhanced-data-table'
import type { DirectoratePayload, ListDirectoratesParams } from '@/utils/api/user-service/models'

import { OrganisationSearchInput } from '@/components/common/OrganisationSearchInput'
import SearchSelect from '@/components/search-select'
import { EnhancedDataTable } from '@/components/tables/enhanced-data-table'
import { AnimatedSwitch } from '@/components/ui/animated-switch'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAuth } from '@/features/authentication/components/auth-context'
import { isAdminUser, isOrganisationAdminUser } from '@/lib/role-guards'
import { handleError } from '@/lib/utils'
import { DirectorateApprovalScenario } from '@/utils/api/leave-service/models'
import { useGetAllOrganisations, useListDirectorates, useUpsertDirectorate } from '@/utils/api/user-service'

const DEFAULT_FORM_STATE = {
  id: '',
  code: '',
  name: '',
  description: '',
  organisationId: '',
  approvalScenario: '',
  active: true,
}

export function DirectoratesPage() {
  const { user: currentUser } = useAuth()
  const isAdmin = isAdminUser(currentUser)
  const isOrgAdmin = isOrganisationAdminUser(currentUser)
  const showOrganisationFilter = isAdmin
  const shouldSelectOrganisation = isAdmin && !isOrgAdmin

  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create')
  const [formState, setFormState] = useState(DEFAULT_FORM_STATE)
  const [organisationFilter, setOrganisationFilter] = useState('all')
  const selectedOrganisationId = useMemo(() => {
    if (!showOrganisationFilter || organisationFilter === 'all') {
      return undefined
    }

    const parsedOrganisationId = Number(organisationFilter)
    return Number.isNaN(parsedOrganisationId) ? undefined : parsedOrganisationId
  }, [organisationFilter, showOrganisationFilter])

  const listParams = useMemo<ListDirectoratesParams>(() => ({
    page: 0,
    size: 25,
    organisationId: selectedOrganisationId,
  }), [selectedOrganisationId])
  const listQuery = useListDirectorates(listParams)
  const organisationsQuery = useGetAllOrganisations({
    query: {
      enabled: showOrganisationFilter,
      staleTime: 30_000,
    },
  })
  const upsertMutation = useUpsertDirectorate({
    mutation: {
      onSuccess: () => {
        setFormState(DEFAULT_FORM_STATE)
        setDialogMode('create')
        setDialogOpen(false)
        void listQuery.refetch()
      },
      onError: (e: import('axios').AxiosError) => handleError(e, 'Unable to save directorate. Please try again.'),
    },
  })

  const directorates = useMemo(() => listQuery.data?.data?.content ?? [], [listQuery.data])
  const organisations = useMemo(() => organisationsQuery.data?.data ?? [], [organisationsQuery.data])
  const canEditOrganisation = isAdmin
  const showOrganisationSelect = canEditOrganisation
  const organisationSelectionRequired = shouldSelectOrganisation && dialogMode === 'create'
  const canSubmit = formState.code.trim() !== ''
    && formState.name.trim() !== ''
    && (!organisationSelectionRequired
      || formState.organisationId.trim() !== ''
      || organisationFilter !== 'all')
  const filteredDirectorates = useMemo(() => {
    if (selectedOrganisationId === undefined) {
      return directorates
    }
    return directorates.filter(directorate => directorate.organisationId === selectedOrganisationId)
  }, [directorates, selectedOrganisationId])
  const organisationOptions = useMemo(() => (
    organisations
      .filter(item => typeof item.id === 'number')
      .map(item => ({
        id: item.id?.toString() ?? '',
        name: item.name ?? `Organisation ${item.id ?? ''}`,
      }))
  ), [organisations])
  const organisationFilterOptions = useMemo(() => (
    [{ id: 'all', name: 'All organisations' }, ...organisationOptions]
  ), [organisationOptions])
  const organisationFilterControl = showOrganisationFilter
    ? (
      <OrganisationSearchInput
        id="directorate-organisation-filter"
        label=""
        placeholder={organisationsQuery.isPending ? 'Loading organisations...' : 'Select organisation'}
        value={organisationFilter}
        onValueChange={value => setOrganisationFilter(value)}
        options={organisationFilterOptions}
        isLoading={organisationsQuery.isPending}
        disabled={organisationsQuery.isPending || organisationsQuery.isError}
      />
    )
    : undefined

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!canSubmit || upsertMutation.isPending) {
      return
    }

    const parsedId = formState.id.trim() === '' ? undefined : Number(formState.id)
    const parsedOrganisationId = canEditOrganisation
      ? (formState.organisationId.trim() !== ''
        ? Number(formState.organisationId)
        : (dialogMode === 'create' && organisationFilter !== 'all' ? Number(organisationFilter) : undefined))
      : undefined

    if (organisationSelectionRequired
      && (parsedOrganisationId === undefined || Number.isNaN(parsedOrganisationId))) {
      return
    }
    const payload: DirectoratePayload = {
      id: parsedId === undefined || Number.isNaN(parsedId) ? undefined : parsedId,
      code: formState.code.trim() || undefined,
      name: formState.name.trim() || undefined,
      description: formState.description.trim() || undefined,
      active: formState.active,
      approvalScenario: formState.approvalScenario.trim() === ''
        ? undefined
        : formState.approvalScenario as DirectorateApprovalScenario,
    }

    if (canEditOrganisation && parsedOrganisationId !== undefined && !Number.isNaN(parsedOrganisationId)) {
      const org = organisationOptions.find(org => org.id === parsedOrganisationId.toString())
      payload.organisationId = parsedOrganisationId
      payload.organisation = org?.name || undefined
    }

    upsertMutation.mutate({ data: payload })
  }

  const openCreateDialog = useCallback(() => {
    setFormState({
      ...DEFAULT_FORM_STATE,
      organisationId: organisationFilter !== 'all' ? organisationFilter : DEFAULT_FORM_STATE.organisationId,
    })
    setDialogMode('create')
    setDialogOpen(true)
  }, [organisationFilter])

  const handleEdit = useCallback((item: DirectoratePayload) => {
    setFormState({
      id: item.id?.toString() ?? '',
      code: item.code ?? '',
      name: item.name ?? '',
      description: item.description ?? '',
      organisationId: item.organisationId?.toString() ?? '',
      approvalScenario: item.approvalScenario ?? '',
      active: item.active ?? true,
    })
    setDialogMode('edit')
    setDialogOpen(true)
  }, [])

  const handleDialogOpenChange = useCallback((open: boolean) => {
    setDialogOpen(open)
    if (!open) {
      setDialogMode('create')
      setFormState(DEFAULT_FORM_STATE)
    }
  }, [])

  const dialogTitle = dialogMode === 'create' ? 'Create Directorate' : 'Edit Directorate'
  const dialogDescription = dialogMode === 'create'
    ? 'Add a new directorate and approval scenario.'
    : 'Update the selected directorate.'
  const submitLabel = dialogMode === 'create' ? 'Create Directorate' : 'Save Changes'

  const columns = useMemo<ColumnConfig<DirectoratePayload>[]>(() => ([
    {
      key: 'code',
      header: 'Code',
    },
    {
      key: 'name',
      header: 'Name',
    },
    {
      key: 'organisation',
      header: 'Organisation',
      cell: value => value as string || '-',
    },
    {
      key: 'approvalScenario',
      header: 'Approval Flow',
      cell: (value) => {
        if (value === DirectorateApprovalScenario.SCENARIO_A) {
          return 'Manager -> Director -> HR -> CEO'
        }
        if (value === DirectorateApprovalScenario.SCENARIO_B) {
          return 'Manager -> HR -> CEO'
        }
        return '-'
      },
    },
    {
      key: 'active',
      header: 'Active',
      type: 'boolean',
    },
    {
      key: 'actions',
      header: '',
      sortable: false,
      filterable: false,
      cell: (_value, row) => (
        <div className="flex justify-end">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => handleEdit(row)}
          >
            Edit
          </Button>
        </div>
      ),
    },
  ]), [handleEdit])

  return (
    <div className="space-y-6">
      <Dialog open={dialogOpen} onOpenChange={handleDialogOpenChange}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{dialogTitle}</DialogTitle>
            <DialogDescription>{dialogDescription}</DialogDescription>
          </DialogHeader>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="directorate-name">Name</Label>
                <Input
                  id="directorate-name"
                  value={formState.name}
                  onChange={event => setFormState(prev => ({ ...prev, name: event.target.value }))}
                  placeholder="Operations"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="directorate-description">Description</Label>
                <Input
                  id="directorate-description"
                  value={formState.description}
                  onChange={event => setFormState(prev => ({ ...prev, description: event.target.value }))}
                  placeholder="Optional directorate description"
                />
              </div>
              {showOrganisationSelect && (
                <div className="space-y-2 md:col-span-2">
                  <OrganisationSearchInput
                    id="directorate-organisation"
                    label="Organisation"
                    placeholder={organisationsQuery.isPending ? 'Loading organisations...' : 'Select organisation'}
                    value={formState.organisationId}
                    onValueChange={value => setFormState(prev => ({ ...prev, organisationId: value }))}
                    options={organisationOptions}
                    isLoading={organisationsQuery.isPending}
                    disabled={organisationsQuery.isPending || organisationsQuery.isError}
                    required={organisationSelectionRequired}
                  />
                </div>
              )}
              <div className="grid gap-4 md:grid-cols-5 md:col-span-2">
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="directorate-code">Code</Label>
                  <Input
                    id="directorate-code"
                    value={formState.code}
                    onChange={event => setFormState(prev => ({ ...prev, code: event.target.value }))}
                    placeholder="OPS"
                  />
                </div>
                <div className="space-y-2 md:col-span-3">
                  <SearchSelect
                    id="directorate-approval-scenario"
                    label="Approval Flow"
                    placeholder="Select scenario"
                    value={formState.approvalScenario}
                    onValueChange={value => setFormState(prev => ({ ...prev, approvalScenario: value }))}
                    options={[
                      { id: DirectorateApprovalScenario.SCENARIO_A, name: 'Manager -> Director -> HR -> CEO' },
                      { id: DirectorateApprovalScenario.SCENARIO_B, name: 'Manager -> HR -> CEO' },
                    ]}
                  />
                </div>
              </div>
              <div className="flex items-center justify-between rounded-lg border px-4 py-3 md:col-span-2">
                <div>
                  <Label htmlFor="directorate-active">Active</Label>
                  <p className="text-sm text-muted-foreground">Enable directorate for use.</p>
                </div>
                <AnimatedSwitch
                  id="directorate-active"
                  checked={formState.active}
                  onCheckedChange={checked => setFormState(prev => ({ ...prev, active: checked }))}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleDialogOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={!canSubmit || upsertMutation.isPending}>
                {upsertMutation.isPending ? 'Saving...' : submitLabel}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Card>
        <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1">
            <CardTitle>Directorates</CardTitle>
            <CardDescription>Review directorate metadata and approval flows.</CardDescription>
          </div>
          <Button type="button" onClick={openCreateDialog}>
            Create Directorate
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {listQuery.isPending && (
            <p className="text-sm text-muted-foreground">Loading directorates...</p>
          )}
          <EnhancedDataTable
            data={filteredDirectorates}
            columns={columns}
            loading={listQuery.isPending}
            emptyStateMessage="No directorates found."
            enableColumnFilters={false}
            enableExport={false}
            customButtons={organisationFilterControl}
          />
        </CardContent>
      </Card>
    </div>
  )
}
